import { Link } from 'react-router-dom';
import { SEOPageLayout } from '@/components/seo/SEOPageLayout';

const faq = [
  { q: 'What is a living tribute?', a: 'A living tribute is a gesture of love, gratitude, or celebration given to someone while they\'re still alive — as opposed to a posthumous memorial. It can be a message, a gift, an event, or a digital keepsake.' },
  { q: 'How is a living tribute different from a eulogy?', a: 'A eulogy is delivered after someone has passed. A living tribute is shared while the person can still hear it, feel it, and respond. It\'s about celebrating someone while they\'re here.' },
  { q: 'Can I create a digital living tribute for free?', a: 'Yes. Say It NOW lets you create a beautiful Living Announcement — a digital tribute with text, photos, and beautiful animations — completely free.' },
  { q: 'What occasions are right for a living tribute?', a: 'Any occasion — or no occasion at all. Birthdays, retirements, and anniversaries work well, but the most meaningful tributes arrive unexpectedly.' },
];

const related = [
  { label: 'Digital Legacy Message', href: '/digital-legacy-message' },
  { label: 'What to Say Before Someone Dies', href: '/what-to-say-before-someone-dies' },
  { label: 'Thank You Message for Parents', href: '/thank-you-message-for-parents' },
  { label: 'Message to Someone You Love', href: '/message-to-someone-you-love' },
];

export default function LivingTributeIdeas() {
  return (
    <SEOPageLayout
      title="Living Tribute Ideas — Celebrate the Living"
      metaDescription="Discover meaningful living tribute ideas to honor the people you love while they're still here. Real examples, creative approaches, and a free digital tribute creator."
      h1="Living Tribute Ideas — Honor Someone While They Can Still Feel It"
      subtitle="Why wait until it's too late? The most meaningful tributes happen while people are alive to receive them."
      faq={faq}
      relatedPages={related}
    >
      <h2>The Problem with Waiting</h2>
      <p>
        We're culturally trained to save our most beautiful words for eulogies — speeches delivered to rooms full of people who already know, about a person who can no longer hear. <strong>A living tribute flips that script.</strong> It says: why not celebrate someone while they can still feel it?
      </p>
      <p>
        Living tributes are gaining momentum as more people recognize the emotional value of expressed appreciation. Whether it's a milestone birthday, a retirement, or simply a moment of clarity — a living tribute creates a memory that both giver and receiver carry forever.
      </p>

      <h2>10 Living Tribute Ideas</h2>

      <h3>1. A Digital Living Announcement</h3>
      <p>
        Use <Link to="/signup">Say It NOW</Link> to create a <strong>heartfelt digital tribute</strong> with beautiful templates, photos, and emotional animation effects. Share it privately or publicly. The recipient gets a keepsake they can revisit forever.
      </p>

      <h3>2. A Video Message Collection</h3>
      <p>Gather short video messages from family and friends. Compile them into a single tribute that plays like a love letter from everyone who matters.</p>

      <h3>3. A "Living Eulogy" Dinner</h3>
      <p>Host a dinner where each guest shares one specific way the honored person impacted their life. It's a eulogy delivered while they can laugh, cry, and hug everyone in the room.</p>

      <h3>4. A Memory Book</h3>
      <p>Collect written memories, photos, and letters from loved ones into a physical or digital book. Say It NOW's <Link to="/faq">Living Tree</Link> feature digitizes this concept beautifully.</p>

      <h3>5. A Gratitude Letter Read Aloud</h3>
      <p>Write a letter detailing specific moments and sacrifices. Read it aloud at a family gathering or send it as a <Link to="/signup">Living Announcement</Link> if in-person feels too vulnerable.</p>

      <h3>6. A Legacy Interview</h3>
      <p>Sit down with the person and ask them about their life — their proudest moments, biggest lessons, and hopes for the future. Record it. It becomes a family treasure.</p>

      <h3>7. A Named Gesture</h3>
      <p>Plant a tree, donate to their favorite cause, or create something in their name. Pair it with a message explaining why.</p>

      <h3>8. A "Why I'm Grateful" Social Post</h3>
      <p>Public tributes on social media can be deeply meaningful — especially when they're specific and authentic rather than performative.</p>

      <h3>9. A Surprise Reunion</h3>
      <p>Bring together people from different chapters of their life. The surprise isn't the gathering — it's realizing how many people they've impacted.</p>

      <h3>10. An Ongoing Tribute</h3>
      <p>Use Say It NOW's auto-compose feature to send regular messages of appreciation — monthly reminders that someone is seen, loved, and valued.</p>

      <h2>Why Digital Tributes Are Powerful</h2>
      <p>
        A digital tribute through <Link to="/signup">Say It NOW</Link> combines the permanence of writing with the beauty of design. Each message becomes a <em>Living Announcement</em> — a <strong>digital legacy message</strong> that can be revealed with stunning animations, shared privately, and preserved forever. It's more than a text message. It's a declaration.
      </p>
    </SEOPageLayout>
  );
}
